
<template>
  <footer class="bg-gray-900 text-white">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8"> <!-- 缩小行间距 py-12 改为 py-8 -->
      <!-- Main Footer Content -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8"> <!-- mb-12 改为 mb-8 -->
        <!-- Company Info -->
        <div class="lg:col-span-2">
          <div class="mb-4"> <!-- mb-6 改为 mb-4 -->
            <h3 
              class="font-bold -2xl font-bold mb-2"
              :class="isChineseLocale ? 'text-xl' : 'text-sm leading-relaxed'"
            >
              {{ t('footer.company.name') }}
            </h3>
            <p v-if="isChineseLocale" class="text-gray-400 text-sm">{{ t('footer.company.englishName') }}</p>
          </div>
          <p class="text-gray-300 mb-4 leading-relaxed"> <!-- mb-6 改为 mb-4 -->
            {{ t('footer.company.description') }}
          </p>
          <div class="flex space-x-4">
            <a 
              v-for="social in socialLinks" 
              :key="social.name"
              :href="social.url"
              :title="social.name"
              class="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors"
            >
              <span class="text-lg">{{ social.icon }}</span>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="lg:col-span-1 lg:pl-8">
          <h4 class="text-lg font-semibold mb-4">{{ t('footer.quickLinks') }}</h4> <!-- mb-6 改为 mb-4 -->
          <ul class="space-y-2"> <!-- space-y-3 改为 space-y-2 -->
            <li>
              <router-link to="/about" class="text-gray-300 hover:text-white transition-colors">
                {{ t('nav.about') }}
              </router-link>
            </li>
            <li>
              <router-link to="/services" class="text-gray-300 hover:text-white transition-colors">
                {{ t('services.title') }}
              </router-link>
            </li>
            <li>
              <router-link to="/projects" class="text-gray-300 hover:text-white transition-colors">
                {{ t('projects.title') }}
              </router-link>
            </li>
            <li>
              <router-link to="/news" class="text-gray-300 hover:text-white transition-colors">
                {{ t('news.title') }}
              </router-link>
            </li>
            <li>
              <router-link to="/contact" class="text-gray-300 hover:text-white transition-colors">
                {{ t('contact.title') }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div class="lg:col-span-1">
          <h4 class="text-lg font-semibold mb-4">{{ t('footer.mainServices') }}</h4> <!-- mb-6 改为 mb-4 -->
          <ul class="space-y-2"> <!-- space-y-3 改为 space-y-2 -->
            <li>
              <router-link to="/services#engineering" class="text-gray-300 hover:text-white transition-colors">
                {{ t('footer.services.engineering') }}
              </router-link>
            </li>
            <li>
              <router-link to="/services#manufacturing" class="text-gray-300 hover:text-white transition-colors">
                {{ t('footer.services.manufacturing') }}
              </router-link>
            </li>
            <li>
              <router-link to="/services#pressure" class="text-gray-300 hover:text-white transition-colors">
                {{ t('footer.services.pressure') }}
              </router-link>
            </li>
            <li>
              <router-link to="/services#consulting" class="text-gray-300 hover:text-white transition-colors">
                {{ t('footer.services.consulting') }}
              </router-link>
            </li>
            <li>
              <router-link to="/services#maintenance" class="text-gray-300 hover:text-white transition-colors">
                {{ t('footer.services.maintenance') }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div class="lg:col-span-2">
          <h4 class="text-lg font-semibold mb-4">{{ t('footer.contactInfo') }}</h4> <!-- mb-6 改为 mb-4 -->
          <div class="space-y-3"> <!-- space-y-4 改为 space-y-3 -->
            <!-- 地址信息 -->
            <div 
              v-for="info in contactInfo.filter(item => item.type === 'address')" 
              :key="`contact-${info.id}`"
              class="flex items-start"
            >
              <MapPinIcon class="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-primary-400" />
              <div class="text-gray-300">
                <div class="text-white font-semibold mb-1">{{ info.title }}</div>
                <div v-html="info.formattedContent"></div>
              </div>
            </div>
            <!-- 电话信息 -->
            <div 
              v-for="info in contactInfo.filter(item => item.type === 'phone')" 
              :key="`contact-${info.id}`"
              class="flex items-start"
            >
              <PhoneIcon class="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-primary-400" />
              <div>
                <div class="text-white font-semibold mb-1">{{ info.title }}</div>
                <a :href="`${info.content}`" class="text-gray-300 hover:text-white transition-colors">
                  {{ info.formattedContent }}
                </a>
              </div>
            </div>
            <!-- 邮箱信息 -->
            <div 
              v-for="info in contactInfo.filter(item => item.type === 'email')" 
              :key="`contact-${info.id}`"
              class="flex items-start"
            >
              <EnvelopeIcon class="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-primary-400" />
              <div>
                <div class="text-white font-semibold mb-1">{{ info.title }}</div>
                <div v-html="`<a href='mailto:${info.content}' class='text-gray-300 hover:text-white transition-colors'>${info.formattedContent}</a>`" 
                     class="text-gray-300 hover:text-white transition-colors">
                </div>
              </div>
            </div>
            <!-- 工作时间信息 -->
            <div 
              v-for="info in contactInfo.filter(item => item.type === 'hours')" 
              :key="`contact-${info.id}`"
              class="flex items-start"
            >
              <ClockIcon class="w-5 h-5 mr-3 mt-1 flex-shrink-0 text-primary-400" />
              <div class="text-gray-300">
                <div class="text-white font-semibold mb-1">{{ info.title }}</div>
                <div v-html="info.formattedContent"></div>
              </div>
            </div>
          </div>
          
          <!-- QR Code
          <div class="mt-4 p-4 bg-gray-800 rounded-lg inline-block"> <!-- mt-6 改为 mt-4
            <div class="w-24 h-24 bg-gray-700 rounded flex items-center justify-center">
              <span class="text-2xl">📱</span>
            </div>
            <p class="text-xs text-gray-400 mt-2 text-center">手机版网站</p>
          </div> -->
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="border-t border-gray-800 pt-6"> <!-- pt-8 改为 pt-6 -->
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="text-gray-400 text-sm flex mb-4 md:mb-0">
            <span>Copyright © {{ currentYear === 2026 ? currentYear : `2026-${currentYear}` }} {{ t('footer.company.name') }} {{ t('footer.copyright') }}</span>
            <span v-if="settings.beian" class="md:ml-4 text-sm">
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" class="hover:text-white transition-colors">
                {{ settings.beian }}
              </a>
            </span>
          </div>
          <div class="flex flex-wrap gap-4 text-sm">
            <a href="#" class="text-gray-400 hover:text-white transition-colors">
              {{ t('footer.links.privacy') }}
            </a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors">
              {{ t('footer.links.sitemap') }}
            </a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors">
              {{ t('footer.links.legal') }}
            </a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors">
              {{ t('footer.links.mobile') }}
            </a>
          </div>
        </div>
        
        <!-- Partner Links -->
        <div class="mt-6 pt-4 border-t border-gray-800"> <!-- mt-8 改为 mt-6，pt-6 改为 pt-4 -->
          <div class="text-gray-400 text-sm flex justify-center mb-3"> <!-- mb-4 改为 mb-3 -->
            {{ t('footer.partners') }}
          </div>
          <div class="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            <a 
              v-for="partner in partners" 
              :key="partner.name"
              :href="partner.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-white transition-colors text-xs px-3 py-2 bg-gray-800 rounded hover:bg-gray-700"
            >
              {{ partner.name }}
            </a>
          </div>
        </div>
      </div>

    </div>

    <!-- Back to Top Button (Mobile) -->
    <button 
      @click="scrollToTop"
      :title="t('footer.backToTop')"
      class="md:hidden fixed bottom-6 right-6 w-12 h-12 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 z-40"
    >
      <ChevronUpIcon class="w-6 h-6" />
    </button>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ChevronUpIcon, 
  ClockIcon
} from '@heroicons/vue/24/outline'
import { useBasicSettings } from '@/composables/useBasicSettings'

const { t, locale } = useI18n()
const route = useRoute()

// 判断是否为中文环境
const isChineseLocale = computed(() => {
  return ['zh-CN', 'zh-TW', 'zh'].includes(locale.value)
})

// 使用 useBasicSettings composable
const { settings, fetchBasicSettings } = useBasicSettings()


// Define interface for contact information
interface ContactInfo {
  id?: number
  type: 'address' | 'phone' | 'email' | 'hours'
  title: string
  content: string
  icon: string
  sort_order: number
  is_active: boolean
  formattedContent?: string
  processedContent?: string
}

// Reactive variables for dynamic content
// const companyName = ref('')
// const companyEnglishName = ref('')
// const siteDescription = ref('')
// const copyrightText = ref('')
const contactInfo = ref<ContactInfo[]>([])
const socialLinks = ref([
  { name: '微信', icon: '💬', url: '#' },
  { name: '微博', icon: '🔥', url: '#' },
  { name: '领英', icon: '💼', url: '#' },
])
const partners = ref<{ name: string, url: string }[]>([
  // { name: '江苏华艾重型装备有限公司', url: 'http://www.huai-equipment.com' },
  // { name: '江苏华宇管件有限公司', url: 'https://www.yz-huayu.com' },
  // { name: '液化空气集团', url: 'https://www.airliquide.com' },
  // { name: '林德集团', url: 'https://www.linde.com' },
  // { name: '空气化工', url: 'https://www.airproducts.com' },
])

const currentYear = computed(() => new Date().getFullYear())


// Fetch basic settings
const fetchBasicSettingsData = async () => {
  try {
    // 获取当前语言
    const lang = (route.params.lang as string) || 'zh'
    const langCode = lang === 'zh' ? 'zh-CN' : lang // 转换为完整的语言代码
    
    // 前端页面使用当前语言获取翻译值
    await fetchBasicSettings(langCode)
    // console.log('[AppFooter] Basic settings loaded:', settings.value)    
    
    // Set partners from footer_links if available
    if (settings.value.footer_links) {
      try {
        let footerLinks;
        if (typeof settings.value.footer_links === 'string') {
          footerLinks = JSON.parse(settings.value.footer_links);
        } else {
          footerLinks = settings.value.footer_links;
        }
        partners.value = Array.isArray(footerLinks) ? footerLinks : [];
      } catch (e) {
        console.error('解析页脚链接失败:', e)
        // 保留默认值
      }
    }
    
    // Set social links from social_media if available
    if (settings.value.social_media) {
      try {
        let socialMediaLinks;
        if (typeof settings.value.social_media === 'string') {
          socialMediaLinks = JSON.parse(settings.value.social_media);
        } else {
          socialMediaLinks = settings.value.social_media;
        }
        socialLinks.value = Array.isArray(socialMediaLinks) ? socialMediaLinks : [];
      } catch (e) {
        console.error('解析社交媒体链接失败:', e)
        // 保留默认值
      }
    }
  } catch (error) {
    console.error('获取基础设置失败:', error)
    // Fallback to default values
    // companyName.value = '安徽艾尔工程建设有限公司'
    // companyEnglishName.value = 'ANHUI AIR ENGINEERING & CONSTRUCTION CO., LTD.'
    // copyrightText.value = `2023-${currentYear.value} 安徽艾尔工程建设有限公司 版权所有`
  }
}

// Fetch contact information
const fetchContactInfo = async () => {
  try {
    const lang = route.params.lang || 'zh'
    const response = await fetch(`/api/contact?lang=${lang}`)
    if (!response.ok) throw new Error('获取联系信息失败')
    
    const data = await response.json()
    if (data.success) {
      // 确保每个联系信息都有formattedContent属性
      // 修复：API返回的是字符串'\n'，不是实际换行符，所以需要匹配字符串'\n'
      contactInfo.value = (data.data || []).map((info: any) => ({
        ...info,
        formattedContent: info.content ? info.content.replace(/\\n/g, '\n').replace(/\n/g, '<br>') : ''
      }))
      // console.log(lang, 'Contact Info:', contactInfo.value)
    } else {
      throw new Error(data.message || '获取联系信息失败')
    }
  } catch (error) {
    console.error('获取联系信息失败:', error)
    // Fallback to default values
    // contactInfo.value = [
    //   {
    //     type: 'address',
    //     title: '地址',
    //     content: '安徽省合肥市创新大道 2800 号\n软件园 G4 幢 E 座 8 楼',
    //     icon: 'map-pin',
    //     sort_order: 1,
    //     is_active: true
    //   },
    //   {
    //     type: 'phone',
    //     title: '电话',
    //     content: '0551-65651202',
    //     icon: 'phone',
    //     sort_order: 2,
    //     is_active: true
    //   },
    //   {
    //     type: 'email',
    //     title: '邮箱',
    //     content: 'ahair@ahair.net',
    //     icon: 'envelope',
    //     sort_order: 3,
    //     is_active: true
    //   }
    // ] as ContactInfo[]
  }
}

// 不再需要展开/收起功能，移除相关变量和方法

// Initialize data on component mount
onMounted(async () => {
  // 初始加载时不立即调用，由 watch 的 immediate: true 处理
  // await Promise.all([fetchBasicSettings(), fetchContactInfo()])
})

// 监听语言变化，自动重新加载页脚数据
watch(() => route.params.lang, async (newLang, oldLang) => {
  // 避免重复加载（当新旧语言相同时）
  if (newLang === oldLang) return
  // console.log(`[AppFooter] Language changed from ${oldLang} to ${newLang}, reloading data...`)
  await Promise.all([fetchBasicSettingsData(), fetchContactInfo()])
}, { immediate: true })

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>

<style scoped>
/* Custom animations */
.hover-lift {
  transition: transform 0.3s ease;
}

.hover-lift:hover {
  transform: translateY(-2px);
}
</style>